import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Award, Download, Calendar, ExternalLink, FileText } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { formatDate } from '../lib/utils';
import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;
const CERT_RIBBON = "https://images.unsplash.com/photo-1741356474365-5f0041f89eaa?crop=entropy&cs=srgb&fm=jpg";

export default function CertificatesPage() {
  const { token } = useAuth();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCertificates = async () => {
      if (!token) return;
      
      try {
        const res = await axios.get(`${API}/certificates`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCertificates(res.data || []);
      } catch (error) {
        console.error('Error fetching certificates:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCertificates();
  }, [token]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold text-white mb-2">
            My Certificates
          </h1>
          <p className="text-zinc-400">
            Your earned certifications and achievements
          </p>
        </div>

        {/* Certificates Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-navigate-surface border border-zinc-800 rounded-xl p-6 animate-pulse">
                <div className="h-32 bg-zinc-800 rounded-lg mb-4" />
                <div className="h-5 bg-zinc-800 rounded w-3/4 mb-2" />
                <div className="h-4 bg-zinc-800 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : certificates.length === 0 ? (
          <div className="text-center py-16">
            <Award className="h-16 w-16 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">No certificates yet</h3>
            <p className="text-zinc-400 mb-6">
              Complete courses to earn your first certificate
            </p>
            <Link to="/courses" className="btn-gold">
              Browse Courses
            </Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {certificates.map((cert) => (
              <div 
                key={cert.certificate_id}
                className="certificate-card rounded-xl overflow-hidden group hover:border-cerebro-accent/50 transition-all"
              >
                {/* Certificate Preview */}
                <div className="relative h-48 bg-gradient-to-br from-cerebro-primary to-cerebro-secondary p-6">
                  {/* Decorative elements */}
                  <div className="absolute top-4 right-4 w-16 h-16 rounded-full bg-cerebro-gold/20 flex items-center justify-center">
                    <Award className="h-8 w-8 text-cerebro-gold" />
                  </div>
                  
                  <div className="absolute bottom-0 left-0 right-0 h-2 bg-gradient-to-r from-cerebro-accent to-cerebro-gold" />
                  
                  <div className="relative z-10">
                    <p className="text-xs text-cerebro-accent uppercase tracking-wider mb-2">
                      Certificate of Completion
                    </p>
                    <h3 className="font-heading text-lg font-semibold text-white line-clamp-2">
                      {cert.title}
                    </h3>
                  </div>
                </div>

                {/* Certificate Details */}
                <div className="p-6 bg-navigate-surface">
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400 flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Issued
                      </span>
                      <span className="text-white">{formatDate(cert.issued_at)}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400 flex items-center gap-2">
                        <Award className="h-4 w-4" />
                        CPD Points
                      </span>
                      <span className="text-cerebro-gold font-medium">{cert.cpd_points}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Certificate ID
                      </span>
                      <span className="text-white font-mono text-xs">{cert.certificate_number}</span>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button className="flex-1 btn-outline py-2 text-sm flex items-center justify-center gap-2">
                      <Download className="h-4 w-4" />
                      Download
                    </button>
                    <button className="flex-1 btn-accent py-2 text-sm flex items-center justify-center gap-2">
                      <ExternalLink className="h-4 w-4" />
                      View
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
